import React from "react"
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis, Tooltip } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "~/shadcn/ui/card"
import { ChartContainer } from "~/shadcn/ui/chart"

type PlatformPoint = { platform: string; count: number }

const PLATFORM_COLORS: Record<string, string> = {
  TikTok: "#000000",
  Instagram: "#E1306C",
  YouTube: "#FF0000",
  Twitch: "#9146FF",
  Other: "#6b7280",
}

function colorForPlatform(platform: string): string {
  return PLATFORM_COLORS[platform] ?? PLATFORM_COLORS.Other
}

export const PlatformBreakdown: React.FC<{ data: PlatformPoint[] }> = ({
  data,
}) => {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-500">
          Requests by Platform
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="flex h-[250px] items-center justify-center text-sm text-gray-400">
            No data for this period
          </div>
        ) : (
          <ChartContainer
            className="h-[250px] w-full"
            config={Object.fromEntries(
              data.map((d) => [
                d.platform,
                { label: d.platform, color: colorForPlatform(d.platform) },
              ])
            )}
          >
            <BarChart
              data={data}
              margin={{ top: 16, right: 8, bottom: 0, left: 0 }}
            >
              <XAxis
                dataKey="platform"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                content={({ payload }) => {
                  if (!payload?.length) return null
                  const entry = payload[0]
                  return (
                    <div className="rounded border bg-white px-2 py-1 text-xs shadow-sm">
                      <div className="font-medium">
                        {entry?.payload?.platform}
                      </div>
                      <div>
                        {Number(entry?.value ?? 0).toLocaleString()} requests
                      </div>
                    </div>
                  )
                }}
              />
              <Bar dataKey="count" isAnimationActive={false}>
                {data.map((entry) => (
                  <Cell
                    key={entry.platform}
                    fill={colorForPlatform(entry.platform)}
                  />
                ))}
                <LabelList
                  dataKey="count"
                  position="top"
                  formatter={(v: number) => v.toLocaleString()}
                  style={{ fontSize: 11 }}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}
